import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Tooltip,
} from '@mui/material';
import { roundMoney } from '../../../../util/roundHelper';
import LockIconWrapper from '../../../components/LockIconWrapper';

// Returns the number of unpaid offline units for a line item
const getUnpaidOfflineUnits = item =>
  (item.paymentReferences || [])
    .filter(ref => !ref.isPaid && ref.paymentType === 'offline')
    .reduce((sum, ref) => sum + ref.count, 0);

// A line item can be marked as paid individually only when it has unpaid
// offline units and no unpaid online units (online must be converted first)
const hasUnpaidOnlineUnits = item =>
  (item.paymentReferences || []).some(
    ref => !ref.isPaid && ref.paymentType === 'online' && ref.count > 0
  );

function PaymentTable({ lineItems, onMarkLineItemPaid = null, lockPaymentBtn = false }) {
  const unpaidItems = lineItems.filter(item => item.paymentReferences.some(ref => !ref.isPaid));

  // Per-row confirmation state: the row awaiting confirmation and its countdown
  const [confirmingId, setConfirmingId] = React.useState(null);
  const [countdown, setCountdown] = React.useState(0);

  React.useEffect(() => {
    if (countdown <= 0) {
      setConfirmingId(null);
      return;
    }
    const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const showActionColumn = typeof onMarkLineItemPaid === 'function';

  const handleRowClick = item => {
    if (confirmingId === item._id && countdown > 0) {
      setConfirmingId(null);
      setCountdown(0);
      onMarkLineItemPaid(item);
    } else {
      setConfirmingId(item._id);
      setCountdown(3);
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{ color: 'red', fontWeight: 'bold', fontSize: '1.2rem', textAlign: 'center' }}
            >
              Anzahl
            </TableCell>
            <TableCell sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Beschreibung</TableCell>
            <TableCell sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Preis</TableCell>
            {showActionColumn && (
              <TableCell sx={{ fontSize: '1.2rem', fontWeight: 'bold', textAlign: 'center' }}>
                Aktion
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {unpaidItems.map(item => {
            const blockedByOnline = hasUnpaidOnlineUnits(item);
            const isConfirming = confirmingId === item._id && countdown > 0;
            return (
              <TableRow key={item._id}>
                <TableCell>
                  <Typography
                    color='error'
                    fontWeight='bold'
                    sx={{
                      color: 'red',
                      fontWeight: 'bold',
                      fontSize: '1.2rem',
                      textAlign: 'center',
                    }}
                  >
                    {getUnpaidOfflineUnits(item)}
                     x
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                  {item.description}
                </TableCell>
                <TableCell sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                  {roundMoney(item.price)}
                </TableCell>
                {showActionColumn && (
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Tooltip
                      title={
                        blockedByOnline
                          ? 'Offene Online-Zahlung zuerst zu Offline-Zahlung konvertieren.'
                          : ''
                      }
                    >
                      <span>
                        <Button
                          variant='contained'
                          color='success'
                          size='small'
                          onClick={() => handleRowClick(item)}
                          disabled={lockPaymentBtn || blockedByOnline}
                        >
                          <LockIconWrapper right={'internalBooking:update:own'}>
                            {isConfirming
                              ? `Bestätigen? (${countdown}s)`
                              : 'Als bezahlt markieren'}
                          </LockIconWrapper>
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default PaymentTable;
